import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import {
  mergeCommercialIntoAcfGroups,
  readCommercialDomains,
  readCommercialDomainsForRow,
} from "./commercialProjectAcf";
import {
  EXPANSION_ACF_GROUPS,
  mergeExpansionIntoAcfGroups,
  projectScalarsFromExpansionGroups,
  readExpansionGroupsFromAcf,
  readSubsegmentSummaryFromAcf,
  syncSubsegmentSummaryToLegacy,
} from "./expansionProjectAcf";
import { normalizeExpansionWorkOrderDisplay } from "./expansionWorkOrderFormat";
import {
  EXPANSION_DOMAIN_LIST,
  EXPANSION_DOMAIN_SCHEMAS,
  EXPANSION_SUMMARY_DOMAIN,
} from "./expansionProjectSchema";
import { resolveInstallationTotals, todayIsoDate } from "./expansionProgressUtils";
import { stripContractDerivedFromConstruction } from "./contractsDerivedFields";

function isBlankFootage(v) {
  return v == null || String(v).trim() === "";
}

function normalizeCompareValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "1" : "0";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  if (Array.isArray(v)) return JSON.stringify(v);
  if (typeof v === "object") return JSON.stringify(v);
  return String(v).trim();
}

/**
 * Keep Overview construction_footage and Construction reported_footage in sync.
 * Prefer the field the user actually changed; do not let a display-seeded
 * reported_footage clobber an Overview edit.
 */
export function syncConstructionAndReportedFootage(subSummary, cons, existingAcf) {
  const persistedSummary = readSubsegmentSummaryFromAcf(existingAcf);
  const persistedCons =
    readCommercialDomains(existingAcf)?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || {};
  const persistedCf = persistedSummary.construction_footage;
  const persistedRf = persistedCons.reported_footage;

  const draftCf = subSummary.construction_footage;
  const draftRf = cons.reported_footage;

  const cfChanged = normalizeCompareValue(draftCf) !== normalizeCompareValue(persistedCf);
  const rfMatchesPersisted =
    normalizeCompareValue(draftRf) === normalizeCompareValue(persistedRf);
  // readExpansionEditDomains seeds empty reported_footage from construction_footage
  const rfLooksSeeded =
    isBlankFootage(persistedRf) &&
    !isBlankFootage(persistedCf) &&
    normalizeCompareValue(draftRf) === normalizeCompareValue(persistedCf);
  const rfChanged = !rfMatchesPersisted && !rfLooksSeeded;

  if (rfChanged && !isBlankFootage(draftRf)) {
    subSummary.construction_footage = draftRf;
    if (!cons.reported_footage_date) {
      cons.reported_footage_date = todayIsoDate();
    }
  } else if (cfChanged) {
    cons.reported_footage = draftCf;
  } else if (isBlankFootage(draftCf) && !isBlankFootage(draftRf)) {
    subSummary.construction_footage = draftRf;
  }
}

export function readExpansionEditDomains(row, acfDomains) {
  const commercial = readCommercialDomainsForRow(row, acfDomains);
  const eng = { ...(commercial[COMMERCIAL_DOMAINS.ENGINEERING] || {}) };
  if (eng.preliminary_route && !eng.route) eng.route = eng.preliminary_route;
  if (eng.route && !eng.preliminary_route) eng.preliminary_route = eng.route;

  const cons = { ...(commercial[COMMERCIAL_DOMAINS.CONSTRUCTION] || {}) };
  const subSummary = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
  if (
    (cons.reported_footage == null || String(cons.reported_footage).trim() === "") &&
    subSummary.construction_footage != null
  ) {
    cons.reported_footage = subSummary.construction_footage;
  }

  return {
    [EXPANSION_SUMMARY_DOMAIN]: { ...subSummary },
    [COMMERCIAL_DOMAINS.ENGINEERING]: eng,
    [COMMERCIAL_DOMAINS.CONSTRUCTION]: cons,
    [COMMERCIAL_DOMAINS.FIBER]: { ...(commercial[COMMERCIAL_DOMAINS.FIBER] || {}) },
    [COMMERCIAL_DOMAINS.ACCOUNTING]: { ...(commercial[COMMERCIAL_DOMAINS.ACCOUNTING] || {}) },
  };
}

export function countExpansionPendingEdits(savedDomains, draftDomains) {
  if (!savedDomains || !draftDomains) return 0;
  let count = 0;
  for (const domainKey of EXPANSION_DOMAIN_LIST) {
    const schema = EXPANSION_DOMAIN_SCHEMAS[domainKey];
    const saved = savedDomains[domainKey] || {};
    const draft = draftDomains[domainKey] || {};
    if (!schema?.fields) continue;
    for (const field of schema.fields) {
      if (field.computed || field.type === "readonly" || field.derivedFromContract) continue;
      if (field.type === "repeater" || field.type === "fiber_az_otdr") {
        if (normalizeCompareValue(saved[field.key]) !== normalizeCompareValue(draft[field.key])) {
          count += 1;
        }
        continue;
      }
      if (field.type === "file" || field.type === "multifile") {
        if (normalizeCompareValue(saved[field.key]) !== normalizeCompareValue(draft[field.key])) {
          count += 1;
        }
        continue;
      }
      if (normalizeCompareValue(saved[field.key]) !== normalizeCompareValue(draft[field.key])) {
        count += 1;
      }
    }
  }
  return count;
}

export function buildExpansionPatchBody(existingAcf, domains) {
  const cons = stripContractDerivedFromConstruction({
    ...(domains[COMMERCIAL_DOMAINS.CONSTRUCTION] || {}),
  });
  const subSummary = resolveInstallationTotals(
    domains[EXPANSION_SUMMARY_DOMAIN] || {},
    cons.daily_progress_reports
  );
  if (subSummary.work_order != null && String(subSummary.work_order).trim() !== "") {
    subSummary.work_order = normalizeExpansionWorkOrderDisplay(subSummary.work_order);
  }
  syncConstructionAndReportedFootage(subSummary, cons, existingAcf);

  let expansionGroups = readExpansionGroupsFromAcf(existingAcf);
  expansionGroups[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY] = subSummary;
  expansionGroups = syncSubsegmentSummaryToLegacy(expansionGroups);

  const eng = { ...(domains[COMMERCIAL_DOMAINS.ENGINEERING] || {}) };
  if (eng.route && !eng.preliminary_route) eng.preliminary_route = eng.route;
  if (eng.preliminary_route && !eng.route) eng.route = eng.preliminary_route;

  expansionGroups[COMMERCIAL_DOMAINS.ENGINEERING] = eng;
  expansionGroups[COMMERCIAL_DOMAINS.CONSTRUCTION] = cons;
  expansionGroups[COMMERCIAL_DOMAINS.FIBER] = { ...(domains[COMMERCIAL_DOMAINS.FIBER] || {}) };
  expansionGroups[COMMERCIAL_DOMAINS.ACCOUNTING] = {
    ...(domains[COMMERCIAL_DOMAINS.ACCOUNTING] || {}),
  };

  let acf_groups_json = mergeExpansionIntoAcfGroups(existingAcf, expansionGroups);
  acf_groups_json = mergeCommercialIntoAcfGroups(acf_groups_json, {
    [COMMERCIAL_DOMAINS.ENGINEERING]: eng,
    [COMMERCIAL_DOMAINS.CONSTRUCTION]: cons,
    [COMMERCIAL_DOMAINS.FIBER]: expansionGroups[COMMERCIAL_DOMAINS.FIBER],
    [COMMERCIAL_DOMAINS.ACCOUNTING]: expansionGroups[COMMERCIAL_DOMAINS.ACCOUNTING],
  });

  const scalars = projectScalarsFromExpansionGroups(readExpansionGroupsFromAcf(acf_groups_json));
  const body = { acf_groups_json, cvin_expansion: true, _source: "expansion_landing" };
  for (const [k, v] of Object.entries(scalars)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "number" && !Number.isFinite(v)) continue;
    body[k] = v;
  }
  return body;
}

export const EXPANSION_FOOTAGE_GRID_FIELD_KEYS = ["construction_footage", "override_footage"];

/** Normalize grid footage input: trim, strip commas; empty string clears. */
export function normalizeExpansionFootageInput(rawValue) {
  if (rawValue == null) return "";
  return String(rawValue).trim().replace(/,/g, "");
}

/**
 * Build PATCH body for Construction ft / Override ft edits from the expansion grid.
 * Updates subsegment_summary (+ legacy stats) and keeps reported_footage in sync for construction.
 */
export function buildExpansionFootageFieldPatch(row, fieldKey, rawValue) {
  if (!EXPANSION_FOOTAGE_GRID_FIELD_KEYS.includes(fieldKey)) return null;
  const nextValue = normalizeExpansionFootageInput(rawValue);
  if (nextValue !== "" && !Number.isFinite(Number(nextValue))) return null;

  const existingAcf =
    row?.acf_groups_json != null &&
    typeof row.acf_groups_json === "object" &&
    !Array.isArray(row.acf_groups_json)
      ? row.acf_groups_json
      : {};

  const commercial = readCommercialDomains(existingAcf);
  const cons = { ...(commercial[COMMERCIAL_DOMAINS.CONSTRUCTION] || {}) };
  const subSummary = { ...readSubsegmentSummaryFromAcf(existingAcf) };
  subSummary[fieldKey] = nextValue;

  if (fieldKey === "construction_footage") {
    cons.reported_footage = nextValue;
  }

  const body = buildExpansionPatchBody(existingAcf, {
    [EXPANSION_SUMMARY_DOMAIN]: subSummary,
    [COMMERCIAL_DOMAINS.ENGINEERING]: {
      ...(commercial[COMMERCIAL_DOMAINS.ENGINEERING] || {}),
    },
    [COMMERCIAL_DOMAINS.CONSTRUCTION]: cons,
    [COMMERCIAL_DOMAINS.FIBER]: { ...(commercial[COMMERCIAL_DOMAINS.FIBER] || {}) },
    [COMMERCIAL_DOMAINS.ACCOUNTING]: {
      ...(commercial[COMMERCIAL_DOMAINS.ACCOUNTING] || {}),
    },
  });
  if (body) body._source = "grid";
  return body;
}
